/*
 * @providesModule SignUpView
 */

import React, {useState, useEffect, useMemo} from 'react';
import {
  Text,
  View,
  BackHandler,
  Platform,
  ScrollView,
  KeyboardAvoidingView,
  TouchableOpacity,
  StyleSheet,
  SafeAreaView,
  NativeModules,
} from 'react-native';
import styles from 'SignUpStyle';
import SkillzSVG from 'SkillzSVG';
import {GenericHeader, ModalBottomSheet} from '@skillz/ui';
import {SMSVerify} from 'SMSVerify';
import {createStyles} from './SignUpViewStyles';
import source_svgs from '../../source_svgs/_source_svgs';
import {_m} from 'INTL';
import {EmailFieldType, SignInSMSProps} from 'SignInView';
import SkillzStyle from 'SkillzStyle';
import EmailSignInView from '../SignInView/EmailSignInView/EmailSignInView';
import WebView from 'react-native-webview';
import {GameThemeBackground} from '../../Components/GameThemeBackground';
import PushButton from 'PushButton';
import LoadingIndicator from 'LoadingIndicator';
import AuthenticationController from 'AuthenticationController';
import NetClient from 'NetClient';
import Tournament from 'SKZTournament';
import SkillzSDK from 'SkillzSDK';
import ClientPropertyConstants from 'ClientPropertyConstants';
import {sendEmailOTP} from 'EmailOTPUtils';
import BannerModal from 'BannerModal';

const SkillzModule = NativeModules.SkillzModule;

interface SignUpProps {
  //can user exit screen
  allowExit: boolean;
  //if after submit can pop back for navigation
  isAfterSubmitScore: boolean;
  //bool to set verification code requested,
  verificationCodeRequested?: boolean;
  //Function to track aws events
  trackEvent: (name: string, params?: Object, metrics?: Object) => void;
  //Props for SMSVerification
  SMSProps: SignInSMSProps;
  //Callback on “Close” button
  onClosePress: () => void;
  //Call back from android back button
  onBackButtonPress: () => void;
  //Call back from sign in press
  onSignInPress: (username: string, password: string) => void;
  isNewUI: boolean;
  gameTheme: {};
  onContinueWithIDPPress?: (
    type: string,
    onDone: () => void,
    tournament: Tournament,
    navigator: any
  ) => void;
  isEmailVerificationActive?: boolean;
  onSignInWithEmailOTP: (
    email: string,
    token: string,
    isSignUp: boolean,
    onDone: ({data, success}: {data: any; success: boolean}) => void
  ) => void;

  // On sign up with email password
  onSignUpPress?: (
    email: string,
    password: string,
    onDone?: (success: boolean) => void,
    tournament?: Tournament,
    navigator?: Navigator
  ) => any;

  //These are to navigate user to certain screen or tournament after sign up
  completion?: () => {};

  // If user is coming from the store deposit screen
  isStore?: boolean;

  // Callback for email code validation to trigger exit deposit flow (for deposit flow with email)
  emailCodeValidation?: () => void;
}

enum RESEND_TIMER {
  EMAIL = 30,
  PHONE = 15,
}

const TEST_IDS = {
  modalTestID: 'sign_up_modal',
  rewardsMessageTestID: 'sign_up_rewards_message',
  ribbonMessageTestID: 'sign_up_progress_message',
  customerSupportMessageTestID: 'sign_up_support_message',
};

const AWS_EVENTS = {
  EXIT_PRESS: 'SIGN_IN_EXIT',
  AREA_CODE_BOX_PRESS: 'ACCOUNT_SMS_SIGNUP_AREACODE_TAP',
  SEND_CODE: 'ACCOUNT_SMS_SIGNUP_SEND',
  SEND_CODE_CALL: 'ACCOUNT_SMS_SIGNUP_CALL_SEND',
  ENTER_CODE: 'ACCOUNT_SMS_SIGNUP_ENTER',
  SMS_PROMPT: 'ACCOUNT_SMS_SIGNUP_PROMPT',
  INFO_PRESS: 'SIGN_IN_INFO',
};

export enum TabType {
  EMAIL = 'email',
  PHONE = 'phone',
  APPLE = 'apple',
}

const SignUpView = (props: SignUpProps) => {
  const [modalVisible, setModalVisible] = useState(false);
  const [verificationCode, setVerificationCode] = useState(
    props.verificationCodeRequested ?? false
  );

  const {statusBarContentStyle} = SkillzStyle.fullStyles;
  const isDarkTheme = statusBarContentStyle === 0;
  const style = createStyles(SkillzStyle.getOverriddenFallbackTheme(), isDarkTheme);

  const {btnTextColorOne, textColorPrimary, textColorTertiary, playButtonGradient} =
    SkillzStyle.getOverriddenFallbackTheme();

  const SKZAPIManager = NativeModules.SKZAPIManager;

  const [showTos, setShowTos] = useState(false);
  const [webViewLoading, setWebViewLoading] = useState(false);
  // const showTabBar = props.tabClientProps.email && props.tabClientProps.phone;

  const [isEmailVerificationActive, setIsEmailVerificationActive] = useState(false);
  const [emailFieldType, setEmailFieldType] = useState(EmailFieldType.OTP);

  // In order to use signInPress directly here
  const [emailUsername, setEmailUsername] = useState('');
  const [password, setPassword] = useState('');

  const [isValidEmail, setIsValidEmail] = useState(false);
  const [otpAlreadySent, setOtpAlreadySent] = useState(false);

  // Track if 409 caused auto-switch from sign-up to sign-in
  const [isActuallySignUp, setIsActuallySignUp] = useState(true);

  //Error Banner Visible
  const [errorBannerVisible, setErrorBannerVisible] = useState<boolean>(false);
  //Server Error Banner Visible
  const [serverErrorVisible, setServerErrorVisible] = useState<boolean>(false);
  // Server success response
  const [successBannerVisible, setSuccessBannerVisible] = useState({visible: false, message: ''});

  // check if email/otp is present in SkillzModule.java
  const isEmailLoginEnabled =
    typeof (SkillzModule as any).loginUserWithEmail === 'function' ? true : false;

  const initialTab = isEmailLoginEnabled
    ? ClientPropertyConstants.getFTUEPreferredSignupMethod()
    : TabType.PHONE;

  const [activeTab, setActiveTab] = useState(initialTab);

  const fetchUser = () => {
    NetClient.getURL('/v1/user', {}, SKZAPIManager.OnError.failSilently)
      .then((user) => {
        setEmailFieldType(user?.password_set ? EmailFieldType.PASSWORD : EmailFieldType.OTP);
      })
      .catch((error: any) => {
        console.debug(`User node found`, error);
      });
  };

  const handleSignInPress = async () => {
    if (emailUsername && password) {
      if (!props?.onSignUpPress) {
        console.error('onSignUpPress handler is missing');
        setServerErrorVisible(true);
        return;
      }
      try {
        const error = await props?.onSignUpPress(emailUsername, password, props?.completion);
        if (error) {
          if (error === 'SIGN_UP_EMAIL_EXISTING_ACCOUNT') {
            // User already has an account, log them in directly with their credentials
            AuthenticationController.onSignInWithUserArchiveCallback(
              emailUsername,
              password,
              false,
              true
            );
          } else {
            setServerErrorVisible(true);
          }
        }
      } catch (error) {
        console.warn('Error in login press', error);
      }
    }
  };

  const [bannerVisible, setBannerVisible] = useState({visible: false, message: ''});

  const handleEmailOtp = async () => {
    if (!isEmailLoginEnabled) {
      return true;
    }

    // If OTP has already been sent, return success without sending again
    if (otpAlreadySent) {
      return true;
    }
    // Clear all previous banner states to prevent conflicts
    setBannerVisible({visible: false, message: ''});
    setErrorBannerVisible(false);
    setServerErrorVisible(false);
    setSuccessBannerVisible({visible: false, message: ''});

    const result = await sendEmailOTP({
      email: emailUsername,
      isSignUp: true,
      displayName: emailUsername,
    });

    // sendEmailOTP now automatically handles 409 and switches to sign-in
    if (!result.success) {
      setBannerVisible({visible: true, message: result.message || 'Failed to send OTP'});
      return false;
    } else {
      setOtpAlreadySent(true);
      setSuccessBannerVisible({visible: true, message: 'OTP sent successfully!'});
    }

    // Update state if auto-switched to sign-in due to 409
    if (result.switchedToSignIn) {
      setIsActuallySignUp(false);
    }

    // OTP sent successfully (may have auto-switched to sign-in if email existed)
    return true;
  };

  const selectEmailFieldType = () => {
    if (emailFieldType === EmailFieldType.PASSWORD && isValidEmail) {
      setEmailFieldType(EmailFieldType.OTP);
      // Only send OTP if it is not sent alkready
      if (!otpAlreadySent) {
        handleEmailOtp();
      }
    } else setEmailFieldType(EmailFieldType.PASSWORD);
  };

  useEffect(() => {
    // fetchUser();
    props.trackEvent(AWS_EVENTS.SMS_PROMPT);
    if (Platform.OS !== 'web') {
      BackHandler.addEventListener('hardwareBackPress', () => {
        if (props?.isNewUI) {
          if (!props.allowExit) {
            return true;
          }
          backPressHandlerForFTUE();
        } else if (props.allowExit) {
          if (props.onBackButtonPress) {
            props.onBackButtonPress();
          }
          if (!props.isAfterSubmitScore) {
            props.onClosePress();
          }
        }
        return true;
      });
    }
    return () => {
      // props.notifySDK(false);
      if (Platform.OS !== 'web') {
        BackHandler.removeEventListener('hardwareBackPress', () => undefined);
      }
    };
  }, [props.allowExit]);

  const backPressHandlerForFTUE = () => {
    if (
      verificationCode &&
      !(props.SMSProps.userAlreadyHaveAccount && props.SMSProps.inputPhoneNumber)
    ) {
      setVerificationCode(false);
    } else if (activeTab === TabType.EMAIL && isEmailVerificationActive) {
      setIsEmailVerificationActive(false);
      isEmailLoginEnabled && isValidEmail
        ? setEmailFieldType(EmailFieldType.OTP)
        : setEmailFieldType(EmailFieldType.PASSWORD);
    } else if (activeTab === TabType.PHONE || props.SMSProps) {
      props.trackEvent(AWS_EVENTS.EXIT_PRESS);
      props?.onBackButtonPress();
    }
  };

  const renderSignUpContainer = () => {
    return (
      <View style={props?.isNewUI ? style.signUpContainerNewUI : style.signUpContainerOldUI}>
        <EmailSignInView
          colorVariant={'dark'}
          onSignInPress={props.onSignInPress}
          // forgotPasswordAction={props.forgotPasswordAction}
          tosValues={{showTos, setShowTos}}
          loading={{
            show: () => props.SMSProps.loading?.show(),
            hide: () => props.SMSProps.loading?.hide(),
          }}
          // email={props.email}
          isNewUI={props?.isNewUI}
          gameTheme={props.gameTheme}
          header={{
            title: _m('Continue with \n Email Address'),
            description: _m('We’ll send a 5-digit code on this email'),
          }}
          isSignUp={isActuallySignUp}
          onEmailVerificationStateChange={setIsEmailVerificationActive}
          setEmailUsername={(email: string) => {
            setEmailUsername(email);
            // reset OTP sent flag when email changes
            if (email !== emailUsername) {
              setOtpAlreadySent(false);
            }
          }}
          handlePassword={setPassword}
          fieldType={emailFieldType}
          time={
            otpAlreadySent
              ? 30
              : emailFieldType === EmailFieldType.OTP
                ? RESEND_TIMER.EMAIL
                : RESEND_TIMER.PHONE
          }
          isEmailVerificationActive={isEmailVerificationActive}
          onSignInWithEmailOTP={props.onSignInWithEmailOTP}
          setIsValidEmail={setIsValidEmail}
          onSignUpPress={props.onSignUpPress}
          onOtpMethodSelected={handleEmailOtp}
          onError={(error: string) => {
            setBannerVisible({visible: true, message: error});
            setIsEmailVerificationActive(false); //  go back to email input screen
            setIsValidEmail(false); // eeset email validation state
            setOtpAlreadySent(false); // eeset OTP sent flag on error
          }}
          codeValidation={props.emailCodeValidation}
        />
      </View>
    );
  };

  const renderHeader = () => {
    return (
      <View style={styles.headerContainer}>
        <GenericHeader
          title={_m('Save Account')}
          leftIcon={props.allowExit || verificationCode ? source_svgs['arrow.svg'] : undefined}
          onLeftPress={() => {
            if (verificationCode) {
              setVerificationCode(false);
            } else {
              props.onBackButtonPress();
            }
          }}
          rightIcon={source_svgs['helpIcon.svg']}
          onRightPress={() => {
            setModalVisible(true);
          }}
          containerStyle={style.headerContainerStyle}
        />
      </View>
    );
  };

  // to render phone input
  const renderSMS = (smsProps: SignInSMSProps) => {
    return (
      <SMSVerify
        containerStyle={styles.backgroundContainer}
        messageText={{
          header: _m(
            props.isNewUI
              ? 'Continue with \n Mobile Number'
              : 'Enter your phone to save your account and play for cash.'
          ),
          body: _m('We’ll send a 5-digit code to this number'),
        }}
        isValidNumber={smsProps.isValidNumber}
        sendSMSVerification={smsProps.sendSMSVerification}
        fetchCountry={smsProps.fetchCountry}
        formatNumber={smsProps.formatNumber}
        getCountryCode={smsProps.getCountryCode}
        getFlag={smsProps.getFlag}
        countryData={smsProps.countryData}
        numberOfInputs={smsProps.numberOfInputs}
        codeValidation={smsProps.codeValidation}
        showTosMessage={false}
        showSmsMarketingCheckbox={true}
        loading={{
          show: () => smsProps.loading?.show(),
          hide: () => smsProps.loading?.hide(),
        }}
        events={{
          countrySelect: AWS_EVENTS.AREA_CODE_BOX_PRESS,
          codeRequest: AWS_EVENTS.SEND_CODE,
          callRequest: AWS_EVENTS.SEND_CODE_CALL,
          codeEnter: AWS_EVENTS.ENTER_CODE,
        }}
        trackEvent={props.trackEvent}
        verificationCodeValues={{verificationCode, setVerificationCode}}
        signInPress={props.onSignInPress}
        allowSignIn={verificationCode ? false : true}
        inputCountry={smsProps.inputCountry}
        inputCountryValue={smsProps.inputCountryValue}
        inputPhoneNumber={smsProps.inputPhoneNumber}
        onPhoneNumberInUse={smsProps.onPhoneNumberInUse}
        isNewUI={props?.isNewUI}
        gameTheme={props.gameTheme}
      />
    );
  };

  const saveAccountBackgroundImage = () => {
    return null;
    // These two .svg files are broken as they include base64 encoded png
    // data inside of them. We should replace them with proper .svg files,
    // or we need to migrate them to be real png files.
    // return (
    //   <SkillzSVG
    //     style={styles.background}
    //     source={Sizes.isTablet() ? 'background_tablet.svg' : 'background.svg'}
    //   />
    // );
  };

  const rewardIcon = () => {
    return (
      <View style={styles.saveAccountIconContainer}>
        <SkillzSVG source={'rewards.svg'} style={styles.rewardsIcon} />
        <Text
          allowFontScaling={false}
          style={styles.imageText}
          testID={TEST_IDS.rewardsMessageTestID}
        >
          {_m('EARN REWARDS')}
        </Text>
      </View>
    );
  };

  const progressIcon = () => {
    return (
      <View style={styles.saveAccountIconContainer}>
        <SkillzSVG source={'ribbon.svg'} style={styles.ribbonIcon} />
        <Text
          allowFontScaling={false}
          style={styles.imageText}
          testID={TEST_IDS.ribbonMessageTestID}
        >
          {_m('SAVE YOUR PROGRESS')}
        </Text>
      </View>
    );
  };

  const supportIcon = () => {
    return (
      <View style={styles.saveAccountIconContainer}>
        <SkillzSVG source={'customer_support.svg'} style={styles.customerSupportIcon} />
        <Text
          allowFontScaling={false}
          style={styles.imageText}
          testID={TEST_IDS.customerSupportMessageTestID}
        >
          {_m('ACCESS SUPPORT')}
        </Text>
      </View>
    );
  };

  const saveAccountBackground = () => {
    return (
      <View>
        {saveAccountBackgroundImage()}
        <View style={styles.saveAccountContainer}>
          {rewardIcon()}
          {progressIcon()}
          {supportIcon()}
        </View>
      </View>
    );
  };

  const renderHelpModal = () => {
    const helpModalTitle = _m('Enter your phone to protect your account.');
    const helpModalBody =
      _m(
        'We require your phone to verify your identity, protect your account, and provide a safe and fun platform for everyone.'
      ) +
      _m(
        'Providing your phone also unlocks access to free rewards, pro tournaments, support, and more!\n\n'
      );
    return (
      <View>
        <ModalBottomSheet
          visible={modalVisible}
          maxHeight={500}
          onRequestClose={() => setModalVisible(false)}
          footerText={_m('Close')}
          footerOnPress={() => setModalVisible(false)}
          footerColor={'#4890E8'}
        >
          <View style={styles.helpModalContainer}>
            <Text allowFontScaling={false} style={styles.helpModalTitle}>
              {helpModalTitle}
            </Text>
            <Text allowFontScaling={false} style={styles.helpModalBody}>
              {helpModalBody}
            </Text>
          </View>
        </ModalBottomSheet>
      </View>
    );
  };

  useEffect(() => {
    if (activeTab === TabType.APPLE) {
      handleSocialEmailLinking();
    }
  }, [activeTab]);

  const handleSocialEmailLinking = () => {
    try {
      if (props?.onContinueWithIDPPress) {
        props?.onContinueWithIDPPress('apple', null, null, SkillzSDK.getNavigator());
      }
    } catch (error) {
      console.warn('Error in federated login press', error);
    }
  };

  const renderErrorBanner = () => {
    if (errorBannerVisible) {
      return (
        <View style={style.bannerContainer}>
          <BannerModal
            title={_m('Account already exists')}
            message={_m('This email address already exists.')}
            numberOfLines={2}
            error={true}
            heightContainer={90}
            onClosePress={() => setErrorBannerVisible(false)}
          />
        </View>
      );
    } else if (serverErrorVisible) {
      return (
        <View style={style.bannerContainer}>
          <BannerModal
            title={_m('Server error')}
            message={_m(
              'Sorry, there is an issue with our sign up service. Please try again later.'
            )}
            numberOfLines={2}
            error={true}
            heightContainer={90}
            onClosePress={() => setServerErrorVisible(false)}
          />
        </View>
      );
    }
    return false;
  };

  const renderBanner = useMemo(() => {
    if (bannerVisible.visible) {
      return (
        <View style={style.bannerContainer}>
          <BannerModal
            title={_m('Error')}
            message={_m(bannerVisible?.message)}
            numberOfLines={2}
            error={true}
            heightContainer={90}
            onClosePress={() => setBannerVisible({visible: false, message: ''})}
          />
        </View>
      );
    } else if (successBannerVisible.visible) {
      return (
        <View style={style.bannerContainer}>
          <BannerModal
            title={_m('Success')}
            message={_m(successBannerVisible?.message)}
            numberOfLines={2}
            error={false}
            heightContainer={90}
            onClosePress={() => setSuccessBannerVisible({visible: false, message: ''})}
          />
        </View>
      );
    }
    return false;
  }, [bannerVisible, successBannerVisible]);

  if (props?.isNewUI) {
    return (
      <KeyboardAvoidingView
        behavior={Platform.OS === 'ios' ? 'padding' : 'height'}
        style={style.container}
      >
        <GameThemeBackground />

        <View style={style.container}>
          {(props.allowExit || verificationCode) && (
            <View style={style.header}>
              <TouchableOpacity onPress={backPressHandlerForFTUE}>
                <SkillzSVG
                  source={isDarkTheme ? 'arrow_left.svg' : 'arrow-left_black.svg'}
                  style={style.backIcon}
                  resizeMode='contain'
                />
              </TouchableOpacity>
              <TouchableOpacity
                onPress={() => {
                  setModalVisible(true);
                  props.trackEvent(AWS_EVENTS.INFO_PRESS);
                }}
              >
                <SkillzSVG
                  source={isDarkTheme ? 'help_circle.svg' : 'help-circle_black.svg'}
                  style={style.infoIcon}
                  tintColor={textColorPrimary.color}
                  fill='transparent'
                  resizeMode='contain'
                />
              </TouchableOpacity>
            </View>
          )}
          {renderErrorBanner()}
          {renderBanner}
          <ScrollView
            contentContainerStyle={style.scrollViewContent}
            showsVerticalScrollIndicator={false}
          >
            {activeTab === TabType.PHONE && props.SMSProps ? renderSMS(props.SMSProps) : null}
            {activeTab === TabType.EMAIL || activeTab === TabType.APPLE
              ? renderSignUpContainer()
              : null}

            {/* Hide this View only when in SMS verification code input state */}
            {/* Hide email in oauth/email is not present */}
            {isEmailLoginEnabled && (
              <>
                {!(
                  (activeTab === TabType.PHONE && verificationCode) ||
                  (activeTab === TabType.EMAIL && isEmailVerificationActive)
                ) && (
                  <View style={style.bottomPaddingContainer}>
                    <View style={style.orContinueWithContainer}>
                      <SkillzSVG
                        source={isDarkTheme ? 'rightLine.svg' : 'leftBlack.svg'}
                        style={style.line}
                      />
                      <Text allowFontScaling={false} style={style.orContinueWithText}>
                        Or continue with
                      </Text>
                      <SkillzSVG
                        source={isDarkTheme ? 'leftLine.svg' : 'rightBlack.svg'}
                        style={style.line}
                      />
                    </View>

                    {/* Social Login Buttons */}
                    <View style={style.socialButtonsContainer}>
                      <TouchableOpacity
                        style={style.socialButton}
                        onPress={() =>
                          activeTab === TabType.PHONE
                            ? setActiveTab(TabType.EMAIL)
                            : setActiveTab(TabType.PHONE)
                        }
                      >
                        <SkillzSVG
                          source={
                            activeTab === TabType.EMAIL
                              ? isDarkTheme
                                ? 'mobile.svg'
                                : 'mobile_white.svg'
                              : isDarkTheme
                                ? 'mail.svg'
                                : 'mail_white.svg'
                          }
                          style={style.socialIcon}
                          resizeMode='contain'
                        />
                        <Text allowFontScaling={false} style={style.socialButtonText}>
                          {activeTab === TabType.EMAIL || activeTab === TabType.APPLE
                            ? 'Phone'
                            : activeTab === TabType.PHONE
                              ? 'Email'
                              : 'Email'}
                        </Text>
                      </TouchableOpacity>

                      {Platform.select({ios: true, macos: true}) &&
                        ClientPropertyConstants.getAppleSignUpEnabledIOS() && (
                          <TouchableOpacity
                            style={style.socialButton}
                            onPress={handleSocialEmailLinking}
                          >
                            <SkillzSVG
                              source={isDarkTheme ? 'apple.svg' : 'apple_white.svg'}
                              style={style.socialIcon}
                              resizeMode='contain'
                            />
                            <Text allowFontScaling={false} style={style.socialButtonText}>
                              Apple
                            </Text>
                          </TouchableOpacity>
                        )}
                    </View>

                    <Text allowFontScaling={false} style={style.normalText}>
                      By continuing, you agree to our{' '}
                      <Text
                        allowFontScaling={false}
                        style={style.linkText}
                        onPress={() => {
                          setWebViewLoading(true);
                          setShowTos(true);
                        }}
                      >
                        User Terms and Conditions
                      </Text>{' '}
                      of Service and confirm that you are at least 18 years old.
                    </Text>
                  </View>
                )}
              </>
            )}

            {activeTab === TabType.EMAIL && isEmailVerificationActive && (
              <View style={style.bottomPaddingContainer}>
                <PushButton
                  colors={playButtonGradient}
                  title='Next'
                  onPress={handleSignInPress}
                  cornerRadius={12}
                  textStyle={[style.pushButtonText, {color: btnTextColorOne.color}]}
                  style={[
                    style.pushButtonStyle,
                    {
                      opacity: !(emailFieldType === EmailFieldType.PASSWORD && password?.length > 3)
                        ? 0.6
                        : 1,
                    },
                  ]}
                  disabled={!(emailFieldType === EmailFieldType.PASSWORD && password?.length > 3)}
                />

                {isValidEmail && isEmailLoginEnabled && (
                  <>
                    <View style={style.orContinueWithContainer}>
                      <SkillzSVG
                        tintColor={textColorTertiary.color}
                        source={'rightLine.svg'}
                        style={style.line}
                      />
                      <Text allowFontScaling={false} style={style.orContinueWithText}>
                        Or
                      </Text>
                      <SkillzSVG
                        tintColor={textColorTertiary.color}
                        source={'leftLine.svg'}
                        style={style.line}
                      />
                    </View>

                    <Text
                      allowFontScaling={false}
                      onPress={selectEmailFieldType}
                      style={style.loginWithText}
                    >
                      Login with{' '}
                      {emailFieldType === EmailFieldType.PASSWORD
                        ? EmailFieldType?.OTP
                        : EmailFieldType.PASSWORD}
                    </Text>
                  </>
                )}
              </View>
            )}
          </ScrollView>
          {showTos && (
            <SafeAreaView style={style.tosView}>
              <View style={style.tosHeaderContainer}>
                <GameThemeBackground />
                <Text allowFontScaling={false} style={style.tosTitleText}>
                  Terms of Use
                </Text>
                <TouchableOpacity
                  onPress={() => {
                    setShowTos(false);
                    setWebViewLoading(false);
                  }}
                >
                  <Text allowFontScaling={false} style={style.tosCloseText}>
                    Close
                  </Text>
                </TouchableOpacity>
              </View>
              {webViewLoading && (
                <View style={style.loadingIndicatorContainer}>
                  <LoadingIndicator
                    titleText=''
                    indicatorStyle={LoadingIndicator.Style.FullScreen}
                    isInline={false}
                    testID='webview-loading-indicator'
                  />
                </View>
              )}
              <WebView
                startInLoadingState
                onLoadStart={() => setWebViewLoading(true)}
                onLoadEnd={() => setWebViewLoading(false)}
                onError={() => setWebViewLoading(false)}
                source={{uri: 'https://skillz.com/legal'}}
              />
            </SafeAreaView>
          )}
          {renderHelpModal()}
        </View>
      </KeyboardAvoidingView>
    );
  }

  return (
    <View style={styles.container} testID={TEST_IDS.modalTestID}>
      {renderHeader()}
      <ScrollView>
        {saveAccountBackground()}
        {renderSMS(props.SMSProps)}
      </ScrollView>
      {renderHelpModal()}
    </View>
  );
};

export default SignUpView;
